import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button, Dialog, Pill, SkeletonRows, TextField } from "@/components/ui";
import { formatMoney, formatRelativeTime } from "@/lib/format";
import { customersRepository, type AccountEntry } from "@/repositories/customersRepository";
import { useAuthStore } from "@/stores/authStore";

/**
 * One customer, and their statement.
 *
 * The statement is the point. "You owe me R240" is an assertion; a list of dated
 * charges each naming the receipt it came from is a record, and the difference
 * between them is whether a disagreement at the counter can be settled. Every
 * entry therefore shows what it was for, not just what it moved.
 *
 * Nothing here edits an entry. A mistaken charge is corrected by an adjustment,
 * the same way a stock error is — a book somebody can rewrite is a book that
 * settles nothing.
 */
export function CustomerDetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const [payment, setPayment] = useState("");
  const [limit, setLimit] = useState("");
  const [reason, setReason] = useState("");

  const canSetLimit = useAuthStore((s) => s.can("customers.change_credit_limit"));
  const queryClient = useQueryClient();

  const customer = useQuery({
    queryKey: ["customers", id],
    queryFn: () => customersRepository.get(id),
  });

  function refresh() {
    setPayment("");
    setLimit("");
    setReason("");
    void queryClient.invalidateQueries({ queryKey: ["customers"] });
  }

  const pay = useMutation({
    mutationFn: () => customersRepository.pay(id, payment, reason.trim()),
    onSuccess: refresh,
  });

  const setCreditLimit = useMutation({
    mutationFn: () => customersRepository.setCreditLimit(id, limit, reason.trim()),
    onSuccess: refresh,
  });

  const data = customer.data;
  const account = data?.account ?? null;

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={data?.name ?? "Customer"}
      description={data?.phone || "No number on file"}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      {customer.isLoading || !data ? (
        <SkeletonRows rows={6} />
      ) : (
        <div className="space-y-5">
          {account ? (
            <div className="rounded-control border border-line bg-surface-2 p-3">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-lg font-semibold tabular-nums text-ink-900">
                  {formatMoney(account.balance)}
                </span>
                <span className="text-sm text-ink-600">
                  of a {formatMoney(account.credit_limit)} limit
                </span>
                {!account.is_active ? (
                  <Pill tone="danger">Suspended</Pill>
                ) : account.is_over_limit ? (
                  <Pill tone="danger">Over limit</Pill>
                ) : (
                  <Pill tone="success">{formatMoney(account.available_credit)} available</Pill>
                )}
              </div>
              {account.suspended_reason && (
                <p className="mt-1 text-sm text-ink-600">{account.suspended_reason}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-ink-600">
              This customer pays cash. Set a credit limit to open an account.
            </p>
          )}

          <div className="flex flex-wrap items-end gap-3">
            {account && (
              <>
                <TextField
                  label="Payment received"
                  value={payment}
                  inputMode="decimal"
                  className="w-36"
                  onChange={(event) => setPayment(event.target.value)}
                  placeholder="0.00"
                />
                <Button
                  variant="primary"
                  loading={pay.isPending}
                  disabled={Number(payment) <= 0}
                  onClick={() => pay.mutate()}
                >
                  Take payment
                </Button>
              </>
            )}

            {canSetLimit && (
              <>
                <TextField
                  label="Credit limit"
                  value={limit}
                  inputMode="decimal"
                  className="w-36"
                  onChange={(event) => setLimit(event.target.value)}
                  placeholder={account?.credit_limit ?? "0.00"}
                />
                <Button
                  loading={setCreditLimit.isPending}
                  disabled={limit.trim().length === 0}
                  onClick={() => setCreditLimit.mutate()}
                >
                  Set limit
                </Button>
              </>
            )}
          </div>

          {(account || canSetLimit) && (
            <TextField
              label="Reason"
              value={reason}
              maxLength={200}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Paid at the counter"
              hint="Recorded against the entry. A limit change is also audited."
            />
          )}

          {(pay.error ?? setCreditLimit.error) != null && (
            <p className="text-sm text-danger-700">
              {(pay.error ?? setCreditLimit.error) instanceof Error
                ? (pay.error ?? setCreditLimit.error)!.message
                : "That could not be recorded."}
            </p>
          )}

          <div>
            <h3 className="mb-2 text-sm font-medium text-ink-900">Statement</h3>
            {data.entries.length === 0 ? (
              <p className="text-sm text-ink-600">Nothing on the book.</p>
            ) : (
              <ul className="divide-y divide-line">
                {data.entries.map((entry) => (
                  <EntryRow key={entry.id} entry={entry} />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}

function EntryRow({ entry }: { entry: AccountEntry }) {
  const increases = Number(entry.amount) > 0;

  return (
    <li className="flex flex-wrap items-center gap-3 py-2">
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-ink-900">
          {entry.reason || (increases ? "Charge" : "Payment")}
        </span>
        <span className="block text-xs text-ink-500">
          {formatRelativeTime(entry.created_at)}
          {entry.by && ` · ${entry.by}`}
        </span>
      </span>

      <span
        className={`text-sm tabular-nums ${increases ? "text-ink-900" : "text-success-700"}`}
      >
        {increases ? "+" : "−"}
        {formatMoney(Math.abs(Number(entry.amount)).toFixed(2))}
      </span>
      <span className="w-20 text-right text-xs tabular-nums text-ink-500">
        {formatMoney(entry.balance_after)}
      </span>
    </li>
  );
}
